(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-axd-newsletter-popup]';
  const STORAGE_PREFIX = 'axdNewsletter';
  const DAY_MS = 24 * 60 * 60 * 1000;

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (_error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (_error) {
        // The popup remains usable when storage is blocked.
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (_error) {
        // Nothing to clean up when storage is blocked.
      }
    },
  };

  const readFutureTimestamp = (key) => {
    const timestamp = Number.parseInt(storage.get(key) || '', 10);

    if (!Number.isFinite(timestamp) || timestamp <= Date.now()) {
      storage.remove(key);
      return 0;
    }

    return timestamp;
  };

  const removeSuccessParameter = () => {
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('axd_signup');
    window.history.replaceState({}, document.title, `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
  };

  const redirectThroughDiscount = (discountCode) => {
    if (!discountCode) return;

    const returnUrl = new URL(window.location.href);
    returnUrl.searchParams.delete('customer_posted');
    returnUrl.searchParams.delete('axd_signup');
    returnUrl.searchParams.set('axd_signup', 'success');
    returnUrl.hash = '';

    const returnPath = `${returnUrl.pathname}${returnUrl.search}`;
    const discountUrl = `/discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent(returnPath)}`;
    window.location.replace(discountUrl);
  };

  const initialize = (root) => {
    if (!root || root.dataset.axdNewsletterInitialized === 'true') return;
    root.dataset.axdNewsletterInitialized = 'true';

    const config = Object.freeze({
      discountPercent: Number.parseInt(root.dataset.discountPercent, 10),
      delayMs: Number.parseInt(root.dataset.delayMs, 10),
      closeDays: Number.parseInt(root.dataset.closeDays, 10),
      subscribedDays: Number.parseInt(root.dataset.subscribedDays, 10),
    });

    if (Object.values(config).some((value) => !Number.isFinite(value))) return;

    const keys = {
      closedUntil: `${STORAGE_PREFIX}ClosedUntil`,
      subscribedUntil: `${STORAGE_PREFIX}SubscribedUntil`,
    };
    const card = root.querySelector('[data-axd-newsletter-card]');
    const form = root.querySelector('form');
    const email = root.querySelector('input[type="email"]');
    const submitButton = root.querySelector('[data-axd-newsletter-submit]');
    const status = root.querySelector('[data-axd-newsletter-status]');
    const formView = root.querySelector('[data-axd-newsletter-form-view]');
    const successView = root.querySelector('[data-axd-newsletter-success-view]');
    const postedSuccess = root.querySelector('[data-axd-posted-successfully]');
    const closeButtons = root.querySelectorAll('[data-axd-newsletter-close]');
    const initialButtonText = submitButton ? submitButton.textContent.trim() : '';
    let previouslyFocused = null;
    let previousBodyOverflow = '';
    let previousBodyPaddingRight = '';
    let isOpen = false;
    let isSuccessView = false;
    let openTimer = null;

    if (!card || !form || !email || !submitButton || !status || !formView || !successView) return;

    const lockBodyScroll = () => {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      previousBodyOverflow = document.body.style.overflow;
      previousBodyPaddingRight = document.body.style.paddingRight;
      document.body.style.overflow = 'hidden';

      if (scrollbarWidth > 0) {
        const currentPadding = Number.parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
        document.body.style.paddingRight = `${currentPadding + scrollbarWidth}px`;
      }
    };

    const unlockBodyScroll = () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.paddingRight = previousBodyPaddingRight;
    };

    const getFocusableElements = () =>
      Array.from(
        root.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.getClientRects().length > 0);

    const showSuccess = () => {
      isSuccessView = true;
      formView.hidden = true;
      successView.hidden = false;
      root.setAttribute('aria-labelledby', 'axd-newsletter-success-title');
    };

    const open = ({ success = false } = {}) => {
      if (isOpen) return;
      isOpen = true;
      isSuccessView = success;
      previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      if (success) showSuccess();

      root.hidden = false;
      root.setAttribute('aria-hidden', 'false');
      lockBodyScroll();

      window.requestAnimationFrame(() => {
        root.classList.add('is-visible');
        const focusTarget = success
          ? successView.querySelector('button, a[href], [tabindex]:not([tabindex="-1"])')
          : email;
        focusTarget?.focus({ preventScroll: true });
      });
    };

    const close = () => {
      if (!isOpen) return;

      if (!isSuccessView) {
        storage.set(keys.closedUntil, String(Date.now() + config.closeDays * DAY_MS));
      }

      isOpen = false;
      root.classList.remove('is-visible');
      root.setAttribute('aria-hidden', 'true');
      unlockBodyScroll();

      const finishClose = () => {
        if (isOpen) return;
        root.hidden = true;
        previouslyFocused?.focus?.({ preventScroll: true });
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finishClose();
      } else {
        window.setTimeout(finishClose, 220);
      }
    };

    const resetSubmitButton = () => {
      submitButton.disabled = false;
      submitButton.textContent = initialButtonText;
    };

    const showValidationError = () => {
      status.textContent = 'Introdu o adresă de e-mail validă.';
      status.classList.add('is-error');
      resetSubmitButton();
    };

    const successParameter = new URL(window.location.href).searchParams.get('axd_signup');

    if (successParameter === 'success') {
      storage.set(keys.subscribedUntil, String(Date.now() + config.subscribedDays * DAY_MS));
      storage.remove(keys.closedUntil);
      removeSuccessParameter();
      open({ success: true });
    } else if (postedSuccess) {
      redirectThroughDiscount(postedSuccess.dataset.discountCode);
      return;
    } else if (status.dataset.serverError === 'true') {
      resetSubmitButton();
      open();
    } else if (readFutureTimestamp(keys.subscribedUntil) || readFutureTimestamp(keys.closedUntil)) {
      return;
    } else {
      openTimer = window.setTimeout(() => open(), config.delayMs);
    }

    closeButtons.forEach((button) => button.addEventListener('click', close));

    root.addEventListener('click', (event) => {
      if (event.target === root) close();
    });

    root.addEventListener('keydown', (event) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        card.setAttribute('tabindex', '-1');
        card.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    email.addEventListener('input', () => {
      if (email.validity.valid) {
        status.textContent = '';
        status.classList.remove('is-error');
      }
    });

    email.addEventListener('invalid', showValidationError);

    form.addEventListener('submit', (event) => {
      if (!form.checkValidity() || !email.validity.valid) {
        event.preventDefault();
        showValidationError();
        email.focus();
        return;
      }

      status.textContent = '';
      status.classList.remove('is-error');
      submitButton.disabled = true;
      submitButton.textContent = 'SE ACTIVEAZĂ REDUCEREA...';
    });

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) resetSubmitButton();
    });

    window.addEventListener('pagehide', () => {
      if (openTimer) window.clearTimeout(openTimer);
    });
  };

  const boot = () => document.querySelectorAll(ROOT_SELECTOR).forEach(initialize);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
